import * as THREE from "three";
import type { MapDoc, EdgeSide } from "../content/schemas.ts";
import { mapById } from "../content/load.ts";
import { makeTerrainCtx, type TerrainCtx } from "../map/terrain-ctx.ts";
import { heightAt, WATER_LEVEL } from "../terrain.ts";
import { disposeGroup } from "../meshkit.ts";
import { smoothstep } from "../daynight.ts";
import { lerp } from "../anim/blend.ts";
import { setEdgeHaze } from "../weather.ts";
import { showEdgeCue, hideEdgeCue } from "./cue.ts";
import { buildStripTile, stripWater, landmarkBlock, boundaryCairn } from "./strip.ts";

// ---------------------------------------------------------------------------
// Seamless map edges — the client-side edge-preview manager.
//
// A map's boundary used to hard-cut into sky. Now, approaching an edge:
//  - LINKED edge (`doc.edges.<side>`): a low-res 3D strip of the NEIGHBOR
//    map's real terrain (its makeTerrainCtx heightAt, translated ±MAP_SIZE)
//    continues past the seam — plus its water, landmark massing for its
//    near-seam structures, boundary cairns along the seam, and a "→ <name>"
//    HUD cue. Never entities (cross-map invisibility stays by construction).
//  - UNLINKED edge: an "endless filler" skirt — the ACTIVE map's heightAt
//    sampled past the bounds (fbm is infinite, features/brush/flats are
//    local, so the continuation is seam-perfect for free) — plus fog that
//    thickens as you close in (setEdgeHaze → weather.ts), so the world
//    dissolves into mist over believable ground, never blue void.
//
// Geometry discipline: strips start EXACTLY at the seam (|coord| = half) and
// dip a fading SINK below the real edge verts — no XZ overlap with the real
// terrain means no poke-through anywhere, and because edge strips and corner
// squares share identical border spans/vertex grids, every internal border is
// watertight by construction. Corners are always filler (the diagonal
// neighbor is unknowable from `edges`); a linked strip's height fades to
// filler over the last END_FADE units before a corner so they meet exactly.
//
// Build is amortized: tiles near the player (along-the-edge window +
// approach hysteresis) are queued and built ≤2 per frame; leaving the area
// (or swapping maps — disposeEdgePreview runs inside disposeWorld) disposes
// them. Each queued job captures its height ctx, so a mid-build swap can
// never sample the wrong map's state. CLIENT-ONLY: nothing here is imported
// by world.ts or the server; no colliders (would desync server movement).
// ---------------------------------------------------------------------------

const STRIP_DEPTH = 150; // past the seam; fog is fully opaque at 135
const TILE_TARGET = 54; // along-edge tile width target (grid-fit per map)
const RES = 5; // world-units per vertex (active terrain is 2.5)
const SINK = 0.25; // seam dip hiding the 2.5-vs-5u vert mismatch line
const SINK_RAMP = 6; // sink fades to 0 this far past the seam
const BLEND = 16; // seam→neighbor height blend distance (independent seeds)
const END_FADE = 24; // linked strip fades to filler near map corners
const ACT_PERP = 150; // player-to-seam distance that activates an edge
const DEACT_PERP = 180; // …and deactivates it (hysteresis)
const ACT_ALONG = 220; // along-edge window around the player
const DEACT_ALONG = 280;
const ACT_CORNER = 240; // Chebyshev distance to the corner point
const DEACT_CORNER = 300;
const BUILD_PER_FRAME = 2;
const REFRESH_DIST = 8; // recompute the wanted set after this much movement
const CAIRN_SPACING = 45;
// Water inset ≥ BLEND: a linked side's water starts only AFTER the ground has
// fully blended to the neighbor's height, so the sheet never floats over / clips
// the still-blending near-seam terrain.
const WATER_INSET_LINKED = BLEND + 2;
const HAZE_FAR = 60; // unlinked-edge fog starts here…
const HAZE_NEAR = 15; // …and peaks here
const HAZE_MAX = 0.65;
const CUE_DIST = 35; // "→ <map>" shows inside this band of a linked edge

/** Seam dip: strips/corners sit SINK below the real edge verts right at the
 *  seam, fading to 0 over SINK_RAMP past it (hides the 2.5-vs-5u vert mismatch
 *  line). ONE definition — edge strips and corner tiles must share it exactly
 *  or a step appears where they meet. */
function sinkAt(u: number): number {
  return SINK * Math.max(0, 1 - u / SINK_RAMP);
}

// Per-side coordinate frame: u = outward distance past the seam (negative =
// inside the map), v = the along-edge coordinate. North is −z (plan 24). This
// axis/sign/offset convention is the RENDERING twin of the portal-transfer
// arrival math in src/server/portals.ts (portalHit) — keep the two in step if
// the edge convention ever changes (a client edge module must NOT import the
// server, so they can't physically share a leaf).
type SideDef = {
  side: EdgeSide;
  pt: (u: number, v: number, half: number) => [number, number];
  out: (x: number, z: number, half: number) => number;
  along: (x: number, z: number) => number;
  off: (size: number) => [number, number]; // neighbor-origin offset, world
};
const SIDES: SideDef[] = [
  { side: "east", pt: (u, v, h) => [h + u, v], out: (x, _z, h) => x - h, along: (_x, z) => z, off: (s) => [s, 0] },
  { side: "west", pt: (u, v, h) => [-h - u, v], out: (x, _z, h) => -x - h, along: (_x, z) => z, off: (s) => [-s, 0] },
  { side: "south", pt: (u, v, h) => [v, h + u], out: (_x, z, h) => z - h, along: (x) => x, off: (s) => [0, s] },
  { side: "north", pt: (u, v, h) => [v, -h - u], out: (_x, z, h) => -z - h, along: (x) => x, off: (s) => [0, -s] },
];

type Link = {
  ctx: TerrainCtx;
  offX: number;
  offZ: number;
  name: string;
  /** Neighbor structure placements translated into the ACTIVE frame, with a
   *  real world-space footprint (w × d) + facing so the massing matches the
   *  building's true size/orientation, not a uniform box. */
  landmarks: { x: number; z: number; w: number; d: number; rot: number }[];
};

/** World-space footprint (w × d) for a neighbor structure placement. `footprint`
 *  (rare, explicit) wins; else derive from the house/doc half-extents (hw/hd);
 *  else a default. `turns` (0..3 = 90° steps) already rides `rot`, so w/d stay
 *  the un-rotated extents. */
function placementFootprint(p: { params?: unknown; footprint?: number }): { w: number; d: number } {
  if (typeof p.footprint === "number") return { w: p.footprint, d: p.footprint };
  const pr = p.params as { hw?: number; hd?: number } | undefined;
  const hw = typeof pr?.hw === "number" ? pr.hw : undefined;
  const hd = typeof pr?.hd === "number" ? pr.hd : undefined;
  if (hw !== undefined || hd !== undefined) {
    const w = (hw ?? hd ?? 3.5) * 2;
    const d = (hd ?? hw ?? 3.5) * 2;
    return { w, d };
  }
  return { w: 7, d: 7 };
}

type SideState = {
  def: SideDef;
  link: Link | null;
  heightFn: (x: number, z: number) => number;
  tileW: number;
  tileCount: number;
};

type Job = { key: string; wanted: (px: number, pz: number) => boolean; build: () => THREE.Object3D };

let root: THREE.Group | null = null;
let sceneRef: THREE.Scene | null = null;
let sides: SideState[] = [];
let half = 0;
let size = 0;
const built = new Map<string, THREE.Object3D>();
const queued = new Set<string>();
let queue: Job[] = [];
let lastX = Infinity;
let lastZ = Infinity;

function makeSideHeightFn(s: SideDef, link: Link | null): (x: number, z: number) => number {
  const h = half;
  return (x, z) => {
    const u = s.out(x, z, h);
    const sink = sinkAt(u);
    const hFill = heightAt(x, z); // continues seamlessly past the bounds
    if (!link) return hFill - sink;
    const v = s.along(x, z);
    const endT = Math.min(1, (h - Math.abs(v)) / END_FADE);
    if (endT <= 0) return hFill - sink; // meets the filler corners exactly
    const [sx, sz] = s.pt(0, v, h);
    const t = smoothstep(0, BLEND, u);
    const nH = link.ctx.heightAt(x - link.offX, z - link.offZ);
    const hLink = t >= 1 ? nH : lerp(heightAt(sx, sz), nH, t);
    return lerp(hFill, hLink, endT) - sink;
  };
}

/** Set up the edge previews for the freshly-built ACTIVE map. Cheap: state +
 *  neighbor TerrainCtx hydration only — geometry builds lazily on approach. */
export function initEdgePreview(scene: THREE.Scene, doc: MapDoc): void {
  disposeEdgePreview();
  sceneRef = scene;
  size = doc.size;
  half = doc.size / 2;
  const tileW = size / Math.max(1, Math.round(size / TILE_TARGET));

  sides = SIDES.map((def) => {
    const to = doc.edges?.[def.side]?.to;
    const ndoc = to ? mapById(to) : undefined;
    let link: Link | null = null;
    if (ndoc) {
      const [offX, offZ] = def.off(size);
      link = {
        ctx: makeTerrainCtx(ndoc, []), // NEVER the neighbor's structure pads —
        // deriving them (setStructureFlatZones) would clobber the ACTIVE map's
        // flatten state; massing on uncarved ground is fine at fog distance.
        offX,
        offZ,
        name: ndoc.name,
        landmarks: (ndoc.structures ?? []).map((p) => {
          const fp = placementFootprint(p);
          return { x: p.x + offX, z: p.z + offZ, w: fp.w, d: fp.d, rot: ((p.turns ?? 0) * Math.PI) / 2 };
        }),
      };
    }
    return {
      def, link, heightFn: makeSideHeightFn(def, link),
      tileW, tileCount: Math.round(size / tileW),
    } satisfies SideState;
  });

  root = new THREE.Group();
  root.name = "edgePreview";
  scene.add(root);
  lastX = Infinity;
  lastZ = Infinity;
}

/** Tear down everything (called from disposeWorld — covers map swaps, which
 *  also kills any queued builds before the next map's state activates). */
export function disposeEdgePreview(): void {
  if (root) {
    sceneRef?.remove(root);
    disposeGroup(root);
  }
  root = null;
  sceneRef = null;
  sides = [];
  built.clear();
  queued.clear();
  queue = [];
  setEdgeHaze(0);
  hideEdgeCue();
}

// ---- Tile builders (each captures its ctx at enqueue time) -------------------

function buildEdgeTile(s: SideState, i: number): THREE.Object3D {
  const v0 = -half + i * s.tileW;
  const [ax0, az0] = s.def.pt(0, v0, half);
  const [ax1, az1] = s.def.pt(STRIP_DEPTH, v0 + s.tileW, half);
  const x0 = Math.min(ax0, ax1), z0 = Math.min(az0, az1);
  const g = new THREE.Group();
  const link = s.link;
  g.add(buildStripTile({
    heightFn: s.heightFn,
    waterLevel: link ? link.ctx.waterLevel : WATER_LEVEL,
    // Linked tiles blend the active waterline into the neighbor's over the same
    // band the height blends, so near-seam (active-dominated) ground isn't tinted
    // "beach" against the neighbor's sea level.
    waterLevelFn: link
      ? (x, z) => lerp(WATER_LEVEL, link.ctx.waterLevel, smoothstep(0, BLEND, s.def.out(x, z, half)))
      : undefined,
    x0, z0, w: Math.abs(ax1 - ax0), d: Math.abs(az1 - az0), res: RES,
  }));

  if (s.link) {
    // Boundary cairns along the seam — just INSIDE the map (u = −0.5, past the
    // movement clamp at half−2 so they're unreachable), on the REAL terrain.
    const first = Math.ceil(v0 / CAIRN_SPACING) * CAIRN_SPACING;
    for (let v = first; v < v0 + s.tileW; v += CAIRN_SPACING) {
      if (half - Math.abs(v) < END_FADE) continue; // not in the corner fades
      const [cx, cz] = s.def.pt(-0.5, v, half);
      const y = heightAt(cx, cz);
      if (y < WATER_LEVEL + 0.2) continue; // no cairns standing in water
      const cairn = boundaryCairn(v);
      cairn.position.set(cx, y, cz);
      g.add(cairn);
    }
    // Landmark massing for the neighbor's structures inside this tile.
    for (const lm of s.link.landmarks) {
      const u = s.def.out(lm.x, lm.z, half);
      const v = s.def.along(lm.x, lm.z);
      if (u <= BLEND || u > STRIP_DEPTH - 5 || v < v0 || v >= v0 + s.tileW) continue;
      g.add(landmarkBlock(lm.x, lm.z, s.heightFn(lm.x, lm.z), lm.w, lm.d, lm.rot));
    }
  }
  return g;
}

// Corner squares are ALWAYS filler terrain, sharing the adjacent strips'
// border spans/grids (u ∈ [0, STRIP_DEPTH] on both axes) so borders are
// watertight. sx/sz = ±1 pick the corner. The corner carries its OWN water
// sheet (active level) so a below-waterline corner shows water the moment the
// corner activates (its Chebyshev window opens before the sides' perpendicular
// one) — the sides deliberately no longer span into the corners, so there's no
// overlap/z-fighting.
function buildCornerTile(sx: number, sz: number): THREE.Object3D {
  const fillerFn = (x: number, z: number) =>
    // Sink against whichever seam is nearer, matching the strips' seam rows.
    heightAt(x, z) - sinkAt(Math.min(Math.abs(x) - half, Math.abs(z) - half));
  const x0 = sx > 0 ? half : -half - STRIP_DEPTH;
  const z0 = sz > 0 ? half : -half - STRIP_DEPTH;
  const g = new THREE.Group();
  g.add(buildStripTile({ heightFn: fillerFn, waterLevel: WATER_LEVEL, x0, z0, w: STRIP_DEPTH, d: STRIP_DEPTH, res: RES }));
  g.add(stripWater(x0, z0, STRIP_DEPTH, STRIP_DEPTH, WATER_LEVEL));
  return g;
}

// Water sheets: every side spans only [−half, half] along its edge — the four
// corner tiles own the corner squares (each with its own water), so the ring
// tiles with no overlap and no z-fighting. Linked sides use the NEIGHBOR's
// water level, inset past the height-blend zone.
function buildSideWater(s: SideState): THREE.Object3D {
  const linked = !!s.link;
  const u0 = linked ? WATER_INSET_LINKED : 0;
  const level = linked ? s.link!.ctx.waterLevel : WATER_LEVEL;
  const [ax0, az0] = s.def.pt(u0, -half, half);
  const [ax1, az1] = s.def.pt(STRIP_DEPTH, half, half);
  return stripWater(
    Math.min(ax0, ax1), Math.min(az0, az1),
    Math.abs(ax1 - ax0), Math.abs(az1 - az0), level,
  );
}

// ---- Per-frame update ---------------------------------------------------------

function refreshWanted(px: number, pz: number): void {
  for (const s of sides) {
    const uP = s.def.out(px, pz, half);
    const alongP = s.def.along(px, pz);
    const act = uP > -ACT_PERP;
    const keep = uP > -DEACT_PERP;

    for (let i = 0; i < s.tileCount; i++) {
      const key = `e:${s.def.side}:${i}`;
      const tc = -half + (i + 0.5) * s.tileW;
      const dv = Math.abs(tc - alongP);
      if (act && dv < ACT_ALONG) enqueue(key,
        (qx, qz) => s.def.out(qx, qz, half) > -DEACT_PERP && Math.abs(tc - s.def.along(qx, qz)) < DEACT_ALONG,
        () => buildEdgeTile(s, i));
      else if (built.has(key) && !(keep && dv < DEACT_ALONG)) disposeTile(key);
    }

    const wKey = `w:${s.def.side}`;
    if (act) enqueue(wKey, (qx, qz) => s.def.out(qx, qz, half) > -DEACT_PERP, () => buildSideWater(s));
    else if (built.has(wKey) && !keep) disposeTile(wKey);
  }

  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const key = `c:${sx}:${sz}`;
      const cd = (qx: number, qz: number) => Math.max(Math.abs(qx - sx * half), Math.abs(qz - sz * half));
      if (cd(px, pz) < ACT_CORNER) enqueue(key, (qx, qz) => cd(qx, qz) < DEACT_CORNER, () => buildCornerTile(sx, sz));
      else if (built.has(key) && cd(px, pz) > DEACT_CORNER) disposeTile(key);
    }
  }
}

function enqueue(key: string, wanted: Job["wanted"], build: Job["build"]): void {
  if (built.has(key) || queued.has(key)) return;
  queued.add(key);
  queue.push({ key, wanted, build });
}

function disposeTile(key: string): void {
  const obj = built.get(key);
  if (!obj) return;
  built.delete(key);
  root?.remove(obj);
  disposeGroup(obj);
}

/** Per-frame: haze + cue (cheap math) every call; wanted-set refresh after
 *  REFRESH_DIST of movement; ≤BUILD_PER_FRAME queued tile builds. The
 *  updateDecoView sibling in the game.ts frame loop. */
export function updateEdgePreview(px: number, pz: number): void {
  if (!root) return;

  // Fog haze toward UNLINKED edges; "→ <map>" cue toward LINKED ones.
  let haze = 0;
  let cue: string | null = null;
  let cueD = Infinity;
  for (const s of sides) {
    const d = -s.def.out(px, pz, half); // distance inside from this seam
    if (s.link) {
      if (d < CUE_DIST && d < cueD) { cueD = d; cue = `→ ${s.link.name}`; }
    } else if (d < HAZE_FAR) {
      haze = Math.max(haze, ((HAZE_FAR - d) / (HAZE_FAR - HAZE_NEAR)) * HAZE_MAX);
    }
  }
  setEdgeHaze(Math.min(HAZE_MAX, haze));
  if (cue) showEdgeCue(cue);
  else hideEdgeCue();

  if (Math.abs(px - lastX) > REFRESH_DIST || Math.abs(pz - lastZ) > REFRESH_DIST) {
    lastX = px;
    lastZ = pz;
    refreshWanted(px, pz);
  }

  for (let n = 0; n < BUILD_PER_FRAME && queue.length; ) {
    const job = queue.shift()!;
    queued.delete(job.key);
    if (!job.wanted(px, pz)) continue; // stale — drifted away before its turn
    const obj = job.build();
    built.set(job.key, obj);
    root.add(obj);
    n++;
  }
}
